import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Box,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const getCurrentDate = () => new Date().toISOString().split("T")[0];

const EchoReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const { handleAdd, resetForm } = useAdd("patient-echo", token);

  // Pagination and search state for patients
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Increased page size for better scrolling
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);

  // Pagination and search state for doctors
  const [doctorPage, setDoctorPage] = useState(1);
  const [doctorPageSize, setDoctorPageSize] = useState(100);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  const [allDoctors, setAllDoctors] = useState([]);
  const [hasMoreDoctors, setHasMoreDoctors] = useState(true);
  const [doctorInputValue, setDoctorInputValue] = useState("");

  const { data: echoTypesData } = useFetchObjects(
    ["echo-types"],
    "echo-types/",
    token
  );

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    refetch: refetchPatients,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate],
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token
  );

  const {
    data: doctorsData,
    isLoading: isLoadingDoctors,
    refetch: refetchDoctors,
  } = useFetchObjects(
    ["users", doctorPage, doctorPageSize, doctorSearchTerm],
    `users/?page=${doctorPage}&page_size=${doctorPageSize}&search=${doctorSearchTerm}`,
    token
  );

  const echoTypes = echoTypesData || [];

  const [echoDetails, setEchoDetails] = useState({
    echoType: "",
    testDate: getCurrentDate(),
    isUrgent: false,
    totalAmount: "",
    patient: null,
    doctor: null,
  });

  // Debounced search for patients
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  // Debounced search for doctors
  const debouncedSetDoctorSearchTerm = useCallback(
    debounce((value) => {
      setDoctorSearchTerm(value);
    }, 300),
    []
  );

  // Handle patients data changes and pagination
  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        // Remove duplicates based on patient ID
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setHasMore(patientsData.next !== null);
    } else {
      setHasMore(false);
    }
  }, [patientsData]);

  // Handle doctors data changes and pagination
  useEffect(() => {
    if (doctorsData?.results) {
      setAllDoctors((prevDoctors) => {
        const newDoctors = [...prevDoctors, ...doctorsData.results];
        // Remove duplicates based on doctor ID
        const uniqueDoctors = newDoctors.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniqueDoctors;
      });
      setHasMoreDoctors(doctorsData.next !== null);
    } else {
      setHasMoreDoctors(false);
    }
  }, [doctorsData]);

  // Fetch initial patients data when component mounts
  useEffect(() => {
    if (!searchTerm) {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    }
  }, []);

  // Fetch initial doctors data when component mounts
  useEffect(() => {
    if (!doctorSearchTerm) {
      setAllDoctors([]);
      setDoctorPage(1);
      setHasMoreDoctors(true);
      refetchDoctors();
    }
  }, []);

  // Handle search term changes for patients
  useEffect(() => {
    if (searchTerm !== "") {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    } else {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    }
  }, [searchTerm, refetchPatients]);

  // Handle search term changes for doctors
  useEffect(() => {
    if (doctorSearchTerm !== "") {
      setAllDoctors([]);
      setDoctorPage(1);
      setHasMoreDoctors(true);
      refetchDoctors();
    } else {
      setAllDoctors([]);
      setDoctorPage(1);
      setHasMoreDoctors(true);
      refetchDoctors();
    }
  }, [doctorSearchTerm, refetchDoctors]);

  // Reset form when resetForm prop changes
  useEffect(() => {
    if (resetForm) {
      setEchoDetails({
        echoType: "",
        testDate: getCurrentDate(),
        isUrgent: false,
        totalAmount: "",
        patient: null,
        doctor: null,
      });
      setPatientInputValue("");
      setDoctorInputValue("");
    }
  }, [resetForm]);

  // Handle patient input change with debounce
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue);
  };

  // Handle doctor input change with debounce
  const handleDoctorInputChange = (event, newInputValue) => {
    setDoctorInputValue(newInputValue);
    debouncedSetDoctorSearchTerm(newInputValue);
  };

  // Handle patient selection
  const handlePatientChange = (event, newValue) => {
    setEchoDetails({ ...echoDetails, patient: newValue });
  };

  // Handle doctor selection
  const handleDoctorChange = (event, newValue) => {
    setEchoDetails({ ...echoDetails, doctor: newValue });
  };

  // Handle infinite scroll for patients
  const handlePatientScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatients
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Handle infinite scroll for doctors
  const handleDoctorScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMoreDoctors &&
      !isLoadingDoctors
    ) {
      setDoctorPage((prevPage) => prevPage + 1);
    }
  };

  // Force update for patients autocomplete
  const forceUpdatePatients = () => {
    setForceUpdate((prev) => prev + 1);
  };

  // Force update for doctors autocomplete
  const forceUpdateDoctors = () => {
    // Similar implementation if needed
  };

  // Handle form field changes
  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setEchoDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    const { patient, doctor, echoType, testDate, totalAmount } = echoDetails;
    let errors = {};

    if (!patient) {
      errors.patient = t("doctor.patientRequired");
    }
    if (!doctor) {
      errors.doctor = t("doctor.doctorRequired");
    }
    if (!echoType) {
      errors.echoType = t("doctor.echoTypeRequired");
    }
    if (!testDate) {
      errors.testDate = t("doctor.testDateRequired");
    }
    if (!totalAmount) {
      errors.totalAmount = t("doctor.totalAmountRequired");
    }

    if (Object.keys(errors).length === 0) {
      const payload = {
        patient: patient.id,
        doctor: doctor.id,
        echo_type: echoType,
        date: testDate,
        is_urgent: echoDetails.isUrgent,
        total_amount: totalAmount,
      };
      handleAdd(payload);
    } else {
      // Handle errors - you might want to display them to the user
      console.error("Form errors:", errors);
    }
  };

  return (
    <Paper
      sx={{
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        mt: 4,
        mb: 4,
        p: 6,
      }}
    >
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("reception.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("reception.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography fontWeight={600} variant="h4" color="primary" gutterBottom>
          {t("doctor.echoReferralForm")}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Patient Autocomplete */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={allPatients}
            getOptionLabel={(option) =>
              `${option.firstname} ${option.lastname || ""}`.trim()
            }
            inputValue={patientInputValue}
            onInputChange={handlePatientInputChange}
            onChange={handlePatientChange}
            value={echoDetails.patient}
            ListboxProps={{
              onScroll: handlePatientScroll,
            }}
            loading={isLoadingPatients}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("doctor.selectPatient")}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingPatients ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Doctor Autocomplete */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={allDoctors}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name}`.trim()
            }
            inputValue={doctorInputValue}
            onInputChange={handleDoctorInputChange}
            onChange={handleDoctorChange}
            value={echoDetails.doctor}
            ListboxProps={{
              onScroll: handleDoctorScroll,
            }}
            loading={isLoadingDoctors}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("doctor.selectDoctor")}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingDoctors ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Echo Type */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label={t("doctor.selectEchoType")}
            value={echoDetails.echoType}
            onChange={handleChange}
            name="echoType"
            variant="outlined"
            SelectProps={{
              native: true,
            }}
          >
            <option value=""></option>
            {echoTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </TextField>
        </Grid>

        {/* Test Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("doctor.testDate")}
            type="date"
            name="testDate"
            value={echoDetails.testDate}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
        </Grid>

        {/* Total Amount */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("doctor.totalAmount")}
            type="number"
            name="totalAmount"
            value={echoDetails.totalAmount}
            onChange={handleChange}
            variant="outlined"
            inputProps={{
              min: 0,
              step: "0.01",
            }}
          />
        </Grid>

        {/* Urgent Switch */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label={t("doctor.isUrgent")}
            name="isUrgent"
            value={echoDetails.isUrgent}
            onChange={handleChange}
            variant="outlined"
            SelectProps={{
              native: true,
            }}
          >
            <option value={false}>{t("doctor.no")}</option>
            <option value={true}>{t("doctor.yes")}</option>
          </TextField>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                fontWeight: 700,
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              {t("doctor.submit")}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EchoReferral;
